/**
 * Выгрузка заказа в Excel (orders-core «Выгрузка заказа в Excel»). Генерация — в браузере,
 * без серверного эндпоинта (бэкенд данные уже отдал в OrderDetail).
 *
 * MVP-формат — ОБЩИЙ (id/услуга/объём/статус/этапы/документы), сервис-независимый. Формат
 * анкеты СОУТ (переиспользование выгрузки формы СОУТ) — отложено в Блок 4; профриски — Блок 6.
 *
 * Без сторонней зависимости: генерируем Excel-совместимый CSV (UTF-8 BOM + разделитель «;»,
 * как ожидает русская локаль Excel). Это допустимый MVP-носитель по постановке Блока 3.
 */
import { SERVICE_LABELS, TEAM_STATUS_LABELS } from '@/lib/orders'
import { downloadBlob } from '@/lib/download'
import { canSubmitAnketa } from '@/lib/sout'
import {
  CHECKLIST_ITEMS,
  CHECKLIST_VALUE_LABELS,
  VEHICLE_USE_LABELS,
  canExportProfriskAnketa,
} from '@/lib/profrisk'
import {
  PROFRISK_INSTRUCTIONS_METHOD_QUESTION,
  PROFRISK_INSTRUCTIONS_QUESTION,
  PROFRISK_INSTRUCTIONS_TITLE,
  PROFRISK_MANUAL_NAME_LABEL,
  PROFRISK_METHOD_CARDS,
} from '@/profrisk/profrisk-copy'
import { TRAINING_STATUS_VIEW, formatOtIndices } from '@/lib/training'
import type {
  OrderDataStatus,
  Service,
  StageDTO,
  DocumentDTO,
  SoutAnketa,
  SoutConditions,
  ProfriskAnketa,
  ProfriskInstructionsMethod,
  ChecklistValue,
  JournalRow,
} from '@/lib/api'

/** Минимальный набор полей заказа для выгрузки (общий для OrderDetail и TeamOrderDetail). */
export interface OrderExportData {
  id: string
  service: Service
  volumeSummary: string
  dataStatus: OrderDataStatus
  createdAt: string
  responsibleName?: string
  stages: StageDTO[]
  documents: DocumentDTO[]
}

/** Строки выгрузки (двумерный массив ячеек) — чистая функция, тестируемая без DOM. */
export function buildOrderExportRows(order: OrderExportData): string[][] {
  const rows: string[][] = []
  rows.push(['Заказ', order.id])
  rows.push(['Услуга', SERVICE_LABELS[order.service] ?? order.service])
  rows.push(['Объём', order.volumeSummary || '—'])
  rows.push(['Статус данных', TEAM_STATUS_LABELS[order.dataStatus] ?? order.dataStatus])
  rows.push(['Создан', order.createdAt])
  if (order.responsibleName) rows.push(['Ответственный', order.responsibleName])

  rows.push([])
  rows.push(['Ход работ'])
  rows.push(['Этап', 'Состояние', 'Ориент. дата'])
  for (const s of order.stages) {
    const state = s.isDone ? 'Завершён' : s.isCurrent ? 'Текущий' : 'Предстоит'
    rows.push([s.label, state, s.targetDate ?? ''])
  }

  rows.push([])
  rows.push(['Документы'])
  rows.push(['Документ', 'Тип', 'Дата'])
  for (const d of order.documents) {
    rows.push([d.title, d.typeName, d.date])
  }
  return rows
}

/** Сериализация в CSV: экранирование «;», кавычек и переводов строк. */
function toCsv(rows: string[][]): string {
  const esc = (value: string): string => {
    const needsQuote = /[";\n\r]/.test(value)
    const escaped = value.replace(/"/g, '""')
    return needsQuote ? `"${escaped}"` : escaped
  }
  return rows.map((r) => r.map(esc).join(';')).join('\r\n')
}

/** Выгрузить заказ в Excel-совместимый CSV (кнопка работает у клиента и команды). */
export function exportOrderToExcel(order: OrderExportData): void {
  const csv = '\uFEFF' + toCsv(buildOrderExportRows(order))
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  downloadBlob(`zakaz-${order.service}-${order.id}.csv`, blob)
}

// --- Блок 4: выгрузка в формате анкеты СОУТ (service-sout «Выгрузка заказа СОУТ в Excel») ---

const GENDER_LABEL: Record<string, string> = { m: 'муж.', f: 'жен.', '': '—' }
const RM_TYPE_LABEL: Record<string, string> = { D: 'Действующее', V: 'Вакансия' }

/** Компенсации (design D3) → читаемый список отмеченных видов ("Нет", если ни одна не отмечена). */
function compensationsLabel(c: SoutConditions['compensations']): string {
  const labels: string[] = []
  if (c.extraPay) labels.push('Доплаты')
  if (c.extraVacation) labels.push('Дополнительный отпуск')
  if (c.shortWeek) labels.push('Сокращённая рабочая неделя')
  if (c.milk) labels.push('Выдача молока (или равноценных продуктов)')
  if (c.earlyPension) labels.push('Досрочная льготная пенсия по старости')
  if (c.medical) labels.push('Медосмотры')
  return labels.length > 0 ? labels.join('; ') : 'Нет'
}

/**
 * Строки выгрузки анкеты СОУТ (организация/комиссия/ответственный/места с условиями) — чистая
 * функция, тестируемая без DOM. Формат функциональный (MVP), не фирменный шаблон.
 */
export function buildSoutAnketaRows(anketa: SoutAnketa, orderId: string): string[][] {
  const rows: string[][] = []
  const org = anketa.organization
  rows.push(['Анкета СОУТ', orderId])

  rows.push([])
  rows.push(['Организация'])
  rows.push(['Полное наименование', org.nameFull])
  rows.push(['ИНН', org.inn])
  rows.push(['КПП', org.kpp])
  rows.push(['ОГРН', org.ogrn])
  rows.push(['Юридический адрес', org.legalAddress])

  rows.push([])
  rows.push(['Комиссия'])
  rows.push(['ФИО', 'Должность', 'Председатель'])
  for (const m of anketa.commission) {
    rows.push([m.fullName, m.position, m.isChair ? 'да' : ''])
  }

  rows.push([])
  rows.push(['Ответственный'])
  rows.push(['ФИО', anketa.responsible.fullName])
  rows.push(['Должность', anketa.responsible.position])
  rows.push(['Телефон', anketa.responsible.phone])
  rows.push(['Email', anketa.responsible.email])

  rows.push([])
  rows.push(['Рабочие места', String(anketa.lines.length)])
  anketa.lines.forEach((l, i) => {
    const c = l.conditions
    rows.push([])
    rows.push([`Рабочее место №${i + 1}`])
    rows.push(['Подразделение', c.departmentName])
    rows.push(['Фактический адрес', c.actualAddress])
    rows.push(['Должность', c.position])
    rows.push(['Тип рабочего места', RM_TYPE_LABEL[c.rmType] ?? c.rmType])
    rows.push(['ФИО сотрудника', l.person.fullName])
    rows.push(['СНИЛС', l.person.snils])
    rows.push(['Пол', GENDER_LABEL[l.person.gender] ?? '—'])
    rows.push(['Сотрудник несовершеннолетний', l.person.isMinor ? 'да' : 'нет'])
    rows.push(['Часы смены', String(c.shiftHours)])
    rows.push(['Часов в неделю', String(c.weekHours)])
    // При schedule==='Иной' выгружаем свободный текст графика (IC-2, minor-2; паритет
    // `sout.html:1949`), иначе — само значение из справочника.
    rows.push(['Режим работы', c.schedule === 'Иной' ? c.scheduleOther || 'Иной' : c.schedule])
    rows.push(['Прошлая СОУТ', c.hadPreviousSout ? 'да' : 'нет'])
    rows.push(['Класс по прошлой СОУТ', c.previousClass])
    rows.push(['Индивидуальный № РМ', c.rmNumber])
    rows.push(['Компенсации', compensationsLabel(c.compensations)])
    rows.push(['Оборудование', c.equipment])
    rows.push(['Материалы', c.materials])
    if (c.transport.length > 0) {
      rows.push([
        'Служебный транспорт',
        'Гос. номер',
        'Год',
        'Разрешённая масса, кг',
        'Масса без нагрузки, кг',
      ])
      for (const t of c.transport) {
        rows.push([t.brand, t.plate, String(t.year), String(t.massLaden), String(t.massUnladen)])
      }
    }
    if (c.accidents.length > 0) {
      rows.push(['Несчастные случаи', 'Тяжесть', 'Дата'])
      for (const a of c.accidents) {
        rows.push([a.description, a.severity, a.date])
      }
    }
  })
  return rows
}

/**
 * Выгрузить анкету СОУТ в Excel-совместимый CSV. Гейт (§3d): без ПД-согласия или при недозаполненной
 * анкете выгрузка не выполняется — функция бросает ошибку (повторный прогон всех проверок).
 */
export function exportSoutAnketaToExcel(anketa: SoutAnketa, orderId: string): void {
  if (!canSubmitAnketa(anketa)) {
    throw new Error(
      'Выгрузка недоступна: заполните все обязательные поля и проставьте согласие на обработку ПД',
    )
  }
  const csv = '\uFEFF' + toCsv(buildSoutAnketaRows(anketa, orderId))
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  downloadBlob(`anketa-sout-${orderId}.csv`, blob)
}

// --- \u0411\u043B\u043E\u043A 6: \u0432\u044B\u0433\u0440\u0443\u0437\u043A\u0430 \u0432 \u0444\u043E\u0440\u043C\u0430\u0442\u0435 \u0430\u043D\u043A\u0435\u0442\u044B \u043F\u0440\u043E\u0444\u0440\u0438\u0441\u043A\u043E\u0432 (service-prof-risks \u00AB\u0412\u044B\u0433\u0440\u0443\u0437\u043A\u0430 \u0432 Excel\u00BB) ---

function checklistLabel(v: ChecklistValue): string {
  return v === 'yes' || v === 'no' ? CHECKLIST_VALUE_LABELS[v] : '\u2014'
}

/** \u041f\u043e\u0434\u043f\u0438\u0441\u0438 \u0441\u043f\u043e\u0441\u043e\u0431\u043e\u0432 \u043f\u0435\u0440\u0435\u0434\u0430\u0447\u0438 \u0441\u043f\u0438\u0441\u043a\u0430 \u0438\u043d\u0441\u0442\u0440\u0443\u043a\u0446\u0438\u0439 (centrika-docs!25); '' \u2014 \u0441\u043f\u043e\u0441\u043e\u0431 \u043d\u0435 \u0432\u044b\u0431\u0440\u0430\u043d. */
const METHOD_LABEL: Record<ProfriskInstructionsMethod, string> = {
  '': '\u2014',
  files: PROFRISK_METHOD_CARDS.files.title,
  manual: PROFRISK_METHOD_CARDS.manual.title,
}

/**
 * \u0421\u0442\u0440\u043E\u043A\u0438 \u0432\u044B\u0433\u0440\u0443\u0437\u043A\u0438 \u0430\u043D\u043A\u0435\u0442\u044B \u043F\u0440\u043E\u0444\u0440\u0438\u0441\u043A\u043E\u0432 (\u043E\u0440\u0433\u0430\u043D\u0438\u0437\u0430\u0446\u0438\u044F/\u043A\u043E\u043D\u0442\u0430\u043A\u0442/\u0447\u0435\u043A-\u043B\u0438\u0441\u0442/\u0431\u043B\u043E\u043A \u0438\u043D\u0441\u0442\u0440\u0443\u043A\u0446\u0438\u0439 \u043F\u043E \u041E\u0422/\u0434\u043E\u043B\u0436\u043D\u043E\u0441\u0442\u0438)
 * \u2014 \u0447\u0438\u0441\u0442\u0430\u044F \u0444\u0443\u043D\u043A\u0446\u0438\u044F, \u0442\u0435\u0441\u0442\u0438\u0440\u0443\u0435\u043C\u0430\u044F \u0431\u0435\u0437 DOM. \u0424\u043E\u0440\u043C\u0430\u0442 \u0444\u0443\u043D\u043A\u0446\u0438\u043E\u043D\u0430\u043B\u044C\u043D\u044B\u0439 MVP (\u0442\u043E\u0447\u043D\u044B\u0439 \u0444\u043E\u0440\u043C\u0430\u0442 \u043E\u0442\u043B\u043E\u0436\u0435\u043D, \u00A73f).
 * \u0411\u043B\u043E\u043A\u0430 \u00AB\u041F\u043E\u0434\u0442\u0432\u0435\u0440\u0436\u0434\u0435\u043D\u0438\u0435 \u0430\u043D\u043A\u0435\u0442\u044B \u043F\u0440\u0435\u0434\u043F\u0440\u0438\u044F\u0442\u0438\u044F\u00BB \u0432 \u0432\u044B\u0433\u0440\u0443\u0437\u043A\u0435 \u043D\u0435\u0442 \u2014 \u043E\u043D \u0443\u0434\u0430\u043B\u0451\u043D \u0438\u0437 \u0444\u043E\u0440\u043C\u044B (centrika-docs!25).
 */
export function buildProfriskAnketaRows(anketa: ProfriskAnketa, orderId: string): string[][] {
  const rows: string[][] = []
  const org = anketa.organization
  rows.push(['\u0410\u043D\u043A\u0435\u0442\u0430 \u043F\u0440\u043E\u0444\u0440\u0438\u0441\u043A\u043E\u0432', orderId])

  rows.push([])
  rows.push(['\u041E\u0440\u0433\u0430\u043D\u0438\u0437\u0430\u0446\u0438\u044F'])
  rows.push(['\u041F\u043E\u043B\u043D\u043E\u0435 \u043D\u0430\u0438\u043C\u0435\u043D\u043E\u0432\u0430\u043D\u0438\u0435', org.nameFull])
  rows.push(['\u0418\u041D\u041D', org.inn])
  rows.push(['\u042E\u0440\u0438\u0434\u0438\u0447\u0435\u0441\u043A\u0438\u0439 \u0430\u0434\u0440\u0435\u0441', org.legalAddress])
  rows.push(['\u0420\u0443\u043A\u043E\u0432\u043E\u0434\u0438\u0442\u0435\u043B\u044C', org.directorName])

  rows.push([])
  rows.push(['\u041A\u043E\u043D\u0442\u0430\u043A\u0442\u043D\u043E\u0435 \u043B\u0438\u0446\u043E'])
  rows.push(['\u0424\u0418\u041E', anketa.contact.fullName])
  rows.push(['\u0422\u0435\u043B\u0435\u0444\u043E\u043D', anketa.contact.phone])
  rows.push(['E-mail', anketa.contact.email])

  rows.push([])
  rows.push(['\u0427\u0435\u043A-\u043B\u0438\u0441\u0442 \u043F\u043E \u043E\u0440\u0433\u0430\u043D\u0438\u0437\u0430\u0446\u0438\u0438'])
  for (const item of CHECKLIST_ITEMS) {
    rows.push([item.label, checklistLabel(anketa.checklist[item.key])])
  }

  // \u0411\u043B\u043E\u043A \u00AB\u0420\u0430\u0437\u0440\u0430\u0431\u043E\u0442\u0430\u043D\u043D\u044B\u0435 \u0438\u043D\u0441\u0442\u0440\u0443\u043A\u0446\u0438\u0438 \u043F\u043E \u043E\u0445\u0440\u0430\u043D\u0435 \u0442\u0440\u0443\u0434\u0430\u00BB (centrika-docs!25): \u043E\u0442\u0432\u0435\u0442 \u0434\u0430/\u043D\u0435\u0442, \u0441\u043F\u043E\u0441\u043E\u0431
  // \u043F\u0435\u0440\u0435\u0434\u0430\u0447\u0438 \u0438 \u043F\u0435\u0440\u0435\u0434\u0430\u043D\u043D\u044B\u0435 \u0434\u0430\u043D\u043D\u044B\u0435 \u2014 \u0444\u0430\u0439\u043B\u044B \u043B\u0438\u0431\u043E \u0440\u0443\u0447\u043D\u043E\u0439 \u0441\u043F\u0438\u0441\u043E\u043A (\u043F\u043E\u043B\u044F \u00AB\u2116\u00BB \u0432 \u0441\u0442\u0440\u043E\u043A\u0435 \u0431\u043E\u043B\u044C\u0448\u0435 \u043D\u0435\u0442).
  rows.push([])
  rows.push([PROFRISK_INSTRUCTIONS_TITLE])
  rows.push([PROFRISK_INSTRUCTIONS_QUESTION, checklistLabel(anketa.instructionsDeveloped)])
  if (anketa.instructionsDeveloped === 'yes') {
    rows.push([PROFRISK_INSTRUCTIONS_METHOD_QUESTION, METHOD_LABEL[anketa.instructionsMethod]])
    if (anketa.instructionsMethod === 'files') {
      if (anketa.instructionFiles.length === 0) {
        rows.push(['\u2014'])
      } else {
        for (const f of anketa.instructionFiles) rows.push([f.name])
      }
    } else if (anketa.instructionsMethod === 'manual') {
      const named = anketa.instructions.filter((ins) => ins.name.trim() !== '')
      if (named.length === 0) {
        rows.push(['\u2014'])
      } else {
        rows.push([PROFRISK_MANUAL_NAME_LABEL])
        for (const ins of named) rows.push([ins.name])
      }
    }
  }

  rows.push([])
  rows.push(['\u0414\u043E\u043B\u0436\u043D\u043E\u0441\u0442\u0438', String(anketa.positions.length)])
  anketa.positions.forEach((pos, i) => {
    rows.push([])
    rows.push([`\u0414\u043E\u043B\u0436\u043D\u043E\u0441\u0442\u044C \u2116${i + 1}`])
    rows.push(['\u041F\u043E\u0434\u0440\u0430\u0437\u0434\u0435\u043B\u0435\u043D\u0438\u0435', pos.departmentName])
    rows.push(['\u0414\u043E\u043B\u0436\u043D\u043E\u0441\u0442\u044C', pos.position])
    rows.push(['\u041A\u043E\u043B\u0438\u0447\u0435\u0441\u0442\u0432\u043E \u0448\u0442\u0430\u0442\u043D\u044B\u0445 \u0435\u0434\u0438\u043D\u0438\u0446', String(pos.headcount)])
    rows.push(['\u0412\u044B\u0435\u0437\u0434\u044B \u043D\u0430 \u0441\u043B\u0443\u0436\u0435\u0431\u043D\u043E\u043C \u0430\u0432\u0442\u043E', VEHICLE_USE_LABELS[pos.vehicleUse]])
    rows.push(['\u041E\u0431\u043E\u0440\u0443\u0434\u043E\u0432\u0430\u043D\u0438\u0435, \u043C\u0430\u0442\u0435\u0440\u0438\u0430\u043B\u044B, \u0441\u044B\u0440\u044C\u0451', pos.equipment])
  })
  return rows
}

/**
 * \u0412\u044B\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u0430\u043D\u043A\u0435\u0442\u0443 \u043F\u0440\u043E\u0444\u0440\u0438\u0441\u043A\u043E\u0432 \u0432 Excel-\u0441\u043E\u0432\u043C\u0435\u0441\u0442\u0438\u043C\u044B\u0439 CSV. \u0413\u0435\u0439\u0442 (\u00A73f): \u043F\u0440\u0438 \u043D\u0435\u0434\u043E\u0437\u0430\u043F\u043E\u043B\u043D\u0435\u043D\u043D\u043E\u0439 \u0430\u043D\u043A\u0435\u0442\u0435
 * (\u0447\u0435\u043A-\u043B\u0438\u0441\u0442/\u0431\u043B\u043E\u043A \u0438\u043D\u0441\u0442\u0440\u0443\u043A\u0446\u0438\u0439/\u0434\u043E\u043B\u0436\u043D\u043E\u0441\u0442\u0438) \u0432\u044B\u0433\u0440\u0443\u0437\u043A\u0430 \u043D\u0435 \u0432\u044B\u043F\u043E\u043B\u043D\u044F\u0435\u0442\u0441\u044F \u2014 \u0444\u0443\u043D\u043A\u0446\u0438\u044F \u0431\u0440\u043E\u0441\u0430\u0435\u0442 \u043E\u0448\u0438\u0431\u043A\u0443.
 */
export function exportProfriskAnketaToExcel(anketa: ProfriskAnketa, orderId: string): void {
  if (!canExportProfriskAnketa(anketa)) {
    throw new Error(
      '\u0412\u044B\u0433\u0440\u0443\u0437\u043A\u0430 \u043D\u0435\u0434\u043E\u0441\u0442\u0443\u043F\u043D\u0430: \u0437\u0430\u043F\u043E\u043B\u043D\u0438\u0442\u0435 \u0430\u043D\u043A\u0435\u0442\u0443 \u0438 \u0434\u043E\u0431\u0430\u0432\u044C\u0442\u0435 \u0445\u043E\u0442\u044F \u0431\u044B \u043E\u0434\u043D\u0443 \u0434\u043E\u043B\u0436\u043D\u043E\u0441\u0442\u044C',
    )
  }
  const csv = '\uFEFF' + toCsv(buildProfriskAnketaRows(anketa, orderId))
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  downloadBlob(`anketa-profrisk-${orderId}.csv`, blob)
}

// --- \u0411\u043B\u043E\u043A 5: \u0432\u044B\u0433\u0440\u0443\u0437\u043A\u0430 \u0436\u0443\u0440\u043D\u0430\u043B\u0430 \u0441\u0440\u043E\u043A\u043E\u0432 (service-training \u00AB\u0412\u044B\u0433\u0440\u0443\u0437\u043A\u0430 \u0436\u0443\u0440\u043D\u0430\u043B\u0430 \u0441\u0440\u043E\u043A\u043E\u0432\u00BB) ---

const TRAINING_SOURCE_LABEL: Record<'order' | 'external', string> = {
  order: '\u0437\u0430\u043A\u0430\u0437 \u0426\u0435\u043D\u0442\u0440\u0438\u043A\u0438',
  external: '\u0432\u043D\u0435\u0448\u043D\u0435\u0435',
}

/**
 * \u0421\u0442\u0440\u043E\u043A\u0438 \u0432\u044B\u0433\u0440\u0443\u0437\u043A\u0438 \u0436\u0443\u0440\u043D\u0430\u043B\u0430 \u0441\u0440\u043E\u043A\u043E\u0432 \u2014 \u041F\u041E\u0414\u0420\u041E\u0411\u041D\u041E \u043F\u043E \u0441\u043E\u0431\u044B\u0442\u0438\u044F\u043C/\u043F\u0440\u043E\u0433\u0440\u0430\u043C\u043C\u0430\u043C (\u00A73e: \u044D\u043A\u0440\u0430\u043D \u043A\u043E\u043C\u043F\u0430\u043A\u0442\u043D\u043E \u043F\u043E \u043B\u044E\u0434\u044F\u043C,
 * Excel \u2014 \u043F\u043E\u0434\u0440\u043E\u0431\u043D\u043E). \u041E\u0434\u043D\u0430 \u0441\u0442\u0440\u043E\u043A\u0430 = \u043E\u0434\u043D\u0430 \u0437\u0430\u043F\u0438\u0441\u044C \u043E\u0431\u0443\u0447\u0435\u043D\u0438\u044F \u0441\u043E\u0442\u0440\u0443\u0434\u043D\u0438\u043A\u0430. \u0421\u043E\u0442\u0440\u0443\u0434\u043D\u0438\u043A\u0438 \u0431\u0435\u0437 \u0437\u0430\u043F\u0438\u0441\u0435\u0439 \u0432\u044B\u0432\u043E\u0434\u044F\u0442\u0441\u044F
 * \u043E\u0442\u0434\u0435\u043B\u044C\u043D\u043E\u0439 \u0441\u0442\u0440\u043E\u043A\u043E\u0439 \u00AB\u041D\u0435\u0442 \u0434\u0430\u043D\u043D\u044B\u0445\u00BB, \u0447\u0442\u043E\u0431\u044B \u043D\u0435 \u0442\u0435\u0440\u044F\u0442\u044C\u0441\u044F. \u0427\u0438\u0441\u0442\u0430\u044F \u0444\u0443\u043D\u043A\u0446\u0438\u044F, \u0442\u0435\u0441\u0442\u0438\u0440\u0443\u0435\u0442\u0441\u044F \u0431\u0435\u0437 DOM.
 */
export function buildTrainingJournalRows(rows: JournalRow[]): string[][] {
  const out: string[][] = []
  out.push([
    '\u0424\u0418\u041E',
    '\u041F\u043E\u0434\u0440\u0430\u0437\u0434\u0435\u043B\u0435\u043D\u0438\u0435',
    '\u041D\u0430\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u0438\u0435',
    '\u041F\u0440\u043E\u0433\u0440\u0430\u043C\u043C\u0430',
    '\u0418\u043D\u0434\u0435\u043A\u0441\u044B \u041E\u0422',
    '\u0414\u0430\u0442\u0430 \u043F\u043E\u0441\u043B\u0435\u0434\u043D\u0435\u0433\u043E \u043E\u0431\u0443\u0447\u0435\u043D\u0438\u044F',
    '\u0414\u0435\u0439\u0441\u0442\u0432\u0443\u0435\u0442 \u0434\u043E',
    '\u0421\u0442\u0430\u0442\u0443\u0441',
    '\u0418\u0441\u0442\u043E\u0447\u043D\u0438\u043A',
  ])
  for (const row of rows) {
    const dep = row.departmentName || '\u0411\u0435\u0437 \u043F\u043E\u0434\u0440\u0430\u0437\u0434\u0435\u043B\u0435\u043D\u0438\u044F'
    if (row.records.length === 0) {
      out.push([row.fullName, dep, '', '', '', '', '', '\u041D\u0435\u0442 \u0434\u0430\u043D\u043D\u044B\u0445', ''])
      continue
    }
    for (const r of row.records) {
      out.push([
        row.fullName,
        dep,
        r.directionName,
        r.programName,
        r.directionSystemCode === 'ot' ? formatOtIndices(r.otIndices) : '',
        r.lastTrainedOn ?? '',
        r.dueOn ?? '',
        TRAINING_STATUS_VIEW[r.status].label,
        TRAINING_SOURCE_LABEL[r.source] ?? r.source,
      ])
    }
  }
  return out
}

/** \u0412\u044B\u0433\u0440\u0443\u0437\u0438\u0442\u044C \u0436\u0443\u0440\u043D\u0430\u043B \u0441\u0440\u043E\u043A\u043E\u0432 \u043E\u0431\u0443\u0447\u0435\u043D\u0438\u044F \u0432 Excel-\u0441\u043E\u0432\u043C\u0435\u0441\u0442\u0438\u043C\u044B\u0439 CSV (\u043F\u043E\u0434\u0440\u043E\u0431\u043D\u043E \u043F\u043E \u0441\u043E\u0431\u044B\u0442\u0438\u044F\u043C). */
export function exportTrainingJournalToExcel(rows: JournalRow[], suffix = 'journal'): void {
  const csv = '\uFEFF' + toCsv(buildTrainingJournalRows(rows))
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  downloadBlob(`obuchenie-${suffix}.csv`, blob)
}
